import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, Edit3, Loader2, Mail, MapPin, Phone, Save, UserRound, X } from "lucide-react";
import {
  profileUpdateStart,
  profileUpdateSuccess,
  profileUpdateFailure,
} from "../../store/slices/userSlice";
import { updateConsumerProfile, getErrorMessage } from "../../api/userAuthApi";
import showToast  from "../../utils/toast";
import { normalizePhoneForApi } from "../../utils/phoneNormalize";
import ManageInterestsSection from "../../components/profile/ManageInterestsSection";

const inputClassName =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#FFD221] focus:ring-4 focus:ring-[#FFD221]/20";

function toDisplayName(user) {
  return [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.name || "Infoboxx User";
}

function toInitials(user) {
  const first = user?.first_name?.trim()?.[0];
  const last = user?.last_name?.trim()?.[0];
  if (first && last) return `${first}${last}`.toUpperCase();
  return (user?.email?.slice(0, 2) || "IB").toUpperCase();
}

function toUserPhone(user) {
  return user?.msisdn || user?.phone_number || "";
}

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { user, profileUpdateStatus } = useSelector((s) => s.infoboxx_consumer);
  const saving = profileUpdateStatus === "loading";

  const [isEditing, setIsEditing] = useState(false);
  const [showRefreshOverlay, setShowRefreshOverlay] = useState(false);
  const [form, setForm] = useState(() => ({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    msisdn: toUserPhone(user),
    location: user?.location || "",
  }));

  const fullName = useMemo(() => toDisplayName(user), [user]);
  const initials = useMemo(() => toInitials(user), [user]);

  const startEdit = () => {
    setForm({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      msisdn: toUserPhone(user),
      location: user?.location || "",
    });
    setIsEditing(true);
  };

  const cancelEdit = () => setIsEditing(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const userId = user?.id ?? user?.user_id;
    if (userId == null || userId === "") {
      showToast.error("Could not read your account. Please sign in again.");
      return;
    }

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      msisdn: normalizePhoneForApi(form.msisdn),
      location: form.location.trim(),
    };

    try {
      dispatch(profileUpdateStart());
      const updated = await updateConsumerProfile(userId, payload);
      dispatch(
        profileUpdateSuccess({
          ...updated,
          msisdn: updated?.msisdn ?? payload.msisdn,
          phone_number: updated?.msisdn ?? updated?.phone_number ?? payload.msisdn,
        }),
      );
      setIsEditing(false);
      showToast.success("Profile updated successfully.");
      setShowRefreshOverlay(true);
      window.setTimeout(() => setShowRefreshOverlay(false), 3000);
    } catch (err) {
      const message = getErrorMessage(err);
      dispatch(profileUpdateFailure(message));
      showToast.error(message);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f4ec] px-4 pb-10 pt-28 sm:px-6 lg:px-8">
      {showRefreshOverlay ? (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-[2px]">
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="w-[min(92vw,420px)] rounded-2xl border border-white/15 bg-white p-6 text-center shadow-2xl"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFD221]/20">
              <Loader2 size={24} className="animate-spin" style={{ color: "#E5B800" }} />
            </div>
            <p className="text-lg font-black text-slate-900">Refreshing your profile...</p>
            <p className="mt-1 text-sm text-slate-600">Applying your latest details and syncing your account view.</p>
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#FFD221]/20 px-3 py-1 text-xs font-semibold text-black">
              <CheckCircle2 size={13} />
              New data is in
            </div>
          </motion.div>
        </div>
      ) : null}
      <div className="mx-auto w-full max-w-8xl">
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="overflow-hidden rounded-3xl border border-[#FFD221]/30 bg-white shadow-[0_24px_60px_-30px_rgba(0,0,0,0.08)]"
        >
          <div className="relative border-b border-[#FFD221]/25 bg-gradient-to-r from-white via-[#fffdf6] to-[#f7f4ec] p-8 sm:p-10">
            <div className="absolute right-0 top-0 h-48 w-48 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#FFD221]/25 blur-2xl" />
            <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FFD221] text-xl font-black text-black ring-1 ring-black/10">
                  {initials}
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-[#9a7b00]">Profile</p>
                  <h1 className="text-2xl font-black text-gray-950 sm:text-3xl">{fullName}</h1>
                  <p className="mt-1 text-sm text-gray-600">Manage your personal details and contact information.</p>
                </div>
              </div>

              {!isEditing ? (
                <button
                  type="button"
                  onClick={startEdit}
                  className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black text-black shadow-sm transition hover:opacity-90"
                  style={{ background: "#FFD221" }}
                >
                  <Edit3 size={16} />
                  Edit profile
                </button>
              ) : null}
            </div>
          </div>

          <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-3">
            <div className="space-y-4 rounded-2xl border border-gray-100 bg-gray-50 p-5">
              <InfoRow icon={<Mail size={16} />} label="Email" value={user?.email || "Not set"} />
              <InfoRow icon={<Phone size={16} />} label="Phone" value={toUserPhone(user) || "Not set"} />
              <InfoRow icon={<MapPin size={16} />} label="Location" value={user?.location || "Not set"} />
              <InfoRow icon={<UserRound size={16} />} label="Status" value={user?.email_verified_at ? "Verified account" : "Pending verification"} />
            </div>

            <div className="lg:col-span-2">
              {!isEditing ? (
                <div className="rounded-2xl border border-slate-100 bg-white p-6">
                  <h2 className="text-lg font-bold text-slate-900">Personal Details</h2>
                  <p className="mt-1 text-sm text-slate-500">Tap edit profile to update your details. Password changes are not needed since account access uses OTP verification.</p>
                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <ReadField label="First name" value={user?.first_name || "-"} />
                    <ReadField label="Last name" value={user?.last_name || "-"} />
                    <ReadField label="Phone number" value={toUserPhone(user) || "-"} />
                    <ReadField label="Location" value={user?.location || "-"} />
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="rounded-2xl border border-slate-100 bg-white p-6">
                  <h2 className="text-lg font-bold text-slate-900">Edit Profile</h2>
                  <p className="mt-1 text-sm text-slate-500">Update your core details below. Password management is intentionally excluded because authentication is OTP-based.</p>

                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <label className="space-y-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">First name</span>
                      <input name="first_name" className={inputClassName} value={form.first_name} onChange={onChange} required />
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Last name</span>
                      <input name="last_name" className={inputClassName} value={form.last_name} onChange={onChange} required />
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone number</span>
                      <input name="msisdn" className={inputClassName} value={form.msisdn} onChange={onChange} placeholder="e.g. 0241234567" />
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Location</span>
                      <input name="location" className={inputClassName} value={form.location} onChange={onChange} placeholder="City, Country" />
                    </label>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                    >
                      <X size={16} />
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black text-black shadow-lg transition hover:opacity-90 disabled:opacity-60"
                      style={{ background: "#FFD221" }}
                    >
                      <Save size={16} />
                      {saving ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          <ManageInterestsSection user={user} />
        </motion.section>
      </div>
    </div>
  );
};

function InfoRow({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white px-3 py-2.5">
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <span className="text-[#000000]">{icon}</span>
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}

function ReadField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}

export default ProfilePage;
